import { isPlatformBrowser } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import {
  afterNextRender,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  NO_ERRORS_SCHEMA,
  PLATFORM_ID,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { assets } from './core/constants/assets.const';
import { coerceCustomSoundFlag } from './core/helpers/notification-sound.helper';
import { walletResponse } from './models/postEnquiry.model';
import { MessagingService } from './services/messaging.service';
import { SwUpdateService } from './services/sw-update.service';
import { UserHeadersComponent } from './shared/components/headers/user-headers.component';

import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { DialogService } from 'primeng/dynamicdialog';
import { filter, map, startWith } from 'rxjs';
import { environment } from '../environments/environment';
import { aiService } from './services/ai.service';
import { AudioService } from './services/audio.service';
import { AuthService } from './services/auth.service';
import { ConfirmDialogService } from './services/confirm-dialog.service';
import { GlobalClickService } from './services/global-click.service';
import { I18nService } from './services/i18n.service';
import { InfographicsService } from './services/infographics.service';
import { MalayalamTtsService } from './services/malayalam-tts.service';
import { ReferralNoticeService } from './services/referral-notice.service';
import { ToasterService } from './services/toaster.service';
import { VoiceAssistantService } from './services/voice-assistant.service';
import { AiCabBookingComponent } from './shared/components/ai-cab-booking/ai-cab-booking.component';
import { GoogleMapComponent } from './shared/components/google-map/google-map.component';
import { AvailableVehicleInfoComponent } from './shared/components/infographics/available-vehicle-info/available-vehicle-info.component';
import { PostEnqInfographicsComponent } from './shared/components/infographics/post-enq-infographics/post-enq-infographics.component';
import { TransportInqInfoComponent } from './shared/components/infographics/transport-inq-info/transport-inq-info.component';
import { TrackingMapComponent } from './shared/components/tracking-map/tracking-map.component';
import { MapWidgetsComponent } from './shared/map-widgets/map-widgets.component';

@Component({
  selector: 'logibids-root',
  imports: [
    RouterOutlet,
    ToastModule,
    HttpClientModule,
    NgxUiLoaderModule,
    UserHeadersComponent,
    ConfirmDialogModule,
    MapWidgetsComponent,
    GoogleMapComponent,
    TrackingMapComponent,
    AiCabBookingComponent,
    AvailableVehicleInfoComponent,
    TransportInqInfoComponent,
    PostEnqInfographicsComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class AppComponent {
  private router = inject(Router);
  private translate = inject(I18nService);
  private messaging = inject(MessagingService);
  private swUpdate = inject(SwUpdateService);
  private dialogService = inject(DialogService);
  private AudioService = inject(AudioService);
  private globalClick = inject(GlobalClickService);

  private meta = inject(Meta);
  private title = inject(Title);
  private aiService = inject(aiService);
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);
  public infographicsService = inject(InfographicsService);
  private readonly malayalamTts = inject(MalayalamTtsService);
  private readonly referralNotice = inject(ReferralNoticeService);
  private readonly toaster = inject(ToasterService);
  private readonly voiceAssistant = inject(VoiceAssistantService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private microphonePromptOpen = false;

  readonly isBrowser = isPlatformBrowser(this.platformId);
  @ViewChild('modulesWrapper') modulesWrapper!: ElementRef<HTMLDivElement>;

  readonly aiOpen = signal(false);
  assetsConst = assets;
  readonly isTrackingRoute = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects.startsWith('/tracking')),
      startWith(false),
    ),
  );

  accessToken = signal<string | null>(null);

  constructor() {
    this.AudioService.register('notification', this.assetsConst.payment_success);
    this.AudioService.register('driver', this.assetsConst.driverAnimation);
    this.setupMeta();
    this.bindSignals();
    this.initBrowserOnly();
    this.globalClick.init();
    this.watchReferralNotices();

    effect(() => {
      if (!this.isBrowser) return;

      if (this.authService.getScroll()?.() === true) {
        queueMicrotask(() => this.scrollToBottom());
      }
    });

    effect(() => {
      if (!this.isBrowser) return;
      const token = localStorage['accessToken'];
      if (this.authService.profileDetails()?.token) {
        this.accessToken.set(this.authService.profileDetails()?.token);
      } else {
        this.accessToken.set(token || undefined);
      }
    });

    effect(() => {
      if (!this.isBrowser) {
        return;
      }
      this.authService.profileDetails();
      this.accessToken();
      untracked(() => this.syncVoiceAssistant());
    });

    effect(() => {
      if (!this.isBrowser) {
        return;
      }
      if (!this.voiceAssistant.microphoneNeedsEnable()) {
        return;
      }
      untracked(() => {
        void this.promptMicrophonePermission();
      });
    });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.syncVoiceAssistant(),
        error: () => undefined,
      });
  }

  /** Arms wake-word only for an authenticated app shell, not public/auth routes. */
  private syncVoiceAssistant(): void {
    if (!this.isBrowser || !environment.voiceWakeEnabled) {
      if (this.isBrowser) {
        this.voiceAssistant.disarm(false);
      }
      return;
    }
    const hasSession = !!(this.authService.profileDetails()?.token || this.accessToken());
    const url = this.router.url;
    const onPublicAuthRoute = url.startsWith('/auth') || url.startsWith('/get-started');
    if (hasSession && !onPublicAuthRoute) {
      this.voiceAssistant.arm();
    } else {
      // Soft-stop on auth/public routes so a Sign In click can keep AudioContext
      // warm until post-login arm(). Logout / 401 still hard-release the mic.
      this.voiceAssistant.disarm(false);
    }
  }

  /**
   * Shows how to turn the microphone on in browser site settings.
   * Permission cannot be granted from the app once the browser has blocked it.
   */
  private async promptMicrophonePermission(): Promise<void> {
    if (this.microphonePromptOpen) {
      return;
    }

    this.microphonePromptOpen = true;
    try {
      const accepted = await this.confirmDialog.showMicrophonePermissionDialog(
        this.translate.translate('Attention'),
        this.translate.translate('microphoneAlert'),
        this.translate.translate('ok'),
        this.translate.translate('cancel'),
      );

      if (accepted) {
        await this.voiceAssistant.retryMicrophoneFromUserGesture();
      } else {
        this.voiceAssistant.dismissMicrophonePrompt();
      }
    } finally {
      this.microphonePromptOpen = false;
    }
  }

  /**
   * Guards can queue a referral notice before this component (and the toast
   * outlet) exists, so flush after the first render and on later navigations.
   */
  private watchReferralNotices(): void {
    if (!this.isBrowser) return;

    afterNextRender(() => this.showReferralNotice());

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.showReferralNotice(),
        error: () => undefined,
      });
  }

  private showReferralNotice(): void {
    const key = this.referralNotice.consumeError();
    if (key) {
      this.toaster.showError(this.translate.translate(key));
    }
  }

  private setupMeta(): void {
    this.title.setTitle('Logibids Client');
    this.meta.addTags([
      { name: 'description', content: 'Find the best services on Logibids' },
      { property: 'og:title', content: 'Logibids Services' },
      { property: 'og:type', content: 'website' },
    ]);
  }

  private bindSignals(): void {
    effect(() => {
      const value = this.aiService.getData()();
      if (value !== null) {
        const prev = untracked(() => this.aiOpen());
        this.aiOpen.set(value);
      }
    });
  }

  private initBrowserOnly(): void {
    if (!this.isBrowser) return;

    const lang = localStorage.getItem('language') || 'en';
    if (this.malayalamTts.shouldUseServerTts(lang)) {
      void this.malayalamTts.warmup();
    }

    this.messaging.requestPermission();
    this.messaging.listenForFrontMessages();
    this.swUpdate.checkForUpdates();

    const message = this.translate.translate('notificationAlert');

    if (Notification.permission === 'denied') {
      alert(message);
    }

    navigator.serviceWorker?.addEventListener('message', e => this.handleSWMessage(e), {
      passive: true,
    });

    this.openAiFromColdStart();
  }

  /** Opens the AI assistant when a new tab was launched from an AI_CHAT notification. */
  private openAiFromColdStart(): void {
    if (!this.isBrowser) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('openAI') !== 'true') return;

    this.aiService.setData(true);

    // Strip the flag so a refresh doesn't reopen the assistant.
    params.delete('openAI');
    const query = params.toString();
    const cleanedUrl = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
    window.history.replaceState({}, '', cleanedUrl);
  }

  private handleSWMessage(event: MessageEvent): void {
    const { action, payload, wallet, label, details } = event.data ?? {};

    switch (action) {
      case 'swDebugLog':
        break;

      case 'navigate': {
        const url = payload?.url;
        if (url) {
          try {
            const urlObj = new URL(url);
            const relativeUrl = urlObj.pathname + urlObj.search + urlObj.hash;
            this.router.navigateByUrl(relativeUrl);
          } catch (e) {
            this.router.navigateByUrl(url); // Fallback
          }
        }
        payload?.notifications?.length && this.messaging.sendMessage(payload.notifications);
        break;
      }

      case 'openAI':
        // AI_CHAT notification clicked while a tab is open: stay on the current
        // route and open the AI assistant (same trigger as the map-widget button).
        this.aiService.setData(true);
        break;

      case 'walletPopUp':
        this.walletErrorPopUp(wallet);
        break;

      case 'playNotificationSound':
      case 'refreshNotification':
        // SW may post boolean or string; coerce so "true" still plays after a false push.
        this.messaging.applyPushSound(
          coerceCustomSoundFlag(event.data?.custom_sound),
          event.data?.type,
        );
        this.messaging.sendMessage({ action: 'refresh' });
        break;

      case 'playNotificationSoundSuccess':
        this.messaging.applyPushSound(true, event.data?.type);
        this.messaging.sendMessage({ action: 'refresh' });
        break;
    }
  }

  private scrollToBottom(): void {
    const el = this.modulesWrapper?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }

  private walletErrorPopUp(data: walletResponse): void {
    import('./modals/wallet-error/wallet-error.component').then(module => {
      this.dialogService.open(module.WalletErrorComponent, {
        width: '27vw',
        modal: true,
        closable: true,
        data: { walletData: data, type: 3 },
      });
    });
  }
}
